import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Search, GraduationCap, ClipboardList, CreditCard, Loader2 } from "lucide-react";

const GROUPS = [
  { key: "students", label: "Students", icon: GraduationCap, tint: "text-violet-600 bg-violet-500/10" },
  { key: "enrollments", label: "Enrollments", icon: ClipboardList, tint: "text-primary bg-primary/10" },
  { key: "payments", label: "Payments", icon: CreditCard, tint: "text-accent bg-accent/10" },
];

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults(null); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: term } });
        setResults(data);
        setOpen(true);
      } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const go = (link) => { setOpen(false); setQ(""); setResults(null); navigate(link); };

  const total = results ? GROUPS.reduce((n, g) => n + (results[g.key]?.length || 0), 0) : 0;

  return (
    <div ref={boxRef} className="relative hidden max-w-md flex-1 sm:block">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        data-testid="global-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results && setOpen(true)}
        placeholder="Search students, enrollment ID, mobile, receipt no..."
        className="h-9 bg-slate-50 pl-9"
      />
      {loading && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}

      {open && results && (
        <div data-testid="search-results" className="absolute left-0 right-0 top-11 z-50 max-h-[70vh] overflow-y-auto rounded-xl border border-border bg-white p-1.5 shadow-lg">
          {total === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="search-empty">No matches for “{q}”.</p>
          ) : (
            GROUPS.map((g) => {
              const items = results[g.key] || [];
              if (!items.length) return null;
              const Icon = g.icon;
              return (
                <div key={g.key} className="mb-1">
                  <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{g.label}</p>
                  {items.map((it) => (
                    <button
                      key={it.id}
                      onClick={() => go(it.link)}
                      data-testid={`search-item-${it.id}`}
                      className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-slate-50"
                    >
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${g.tint}`}><Icon className="h-4 w-4" /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-secondary">{it.label}</span>
                        <span className="block truncate text-xs text-muted-foreground">{it.sub}</span>
                      </span>
                    </button>
                  ))}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
